import React from 'react';
import { Star, Quote } from 'lucide-react';

const TestimonialsSection = () => {
  const testimonials = [
  {
    name: "Sarah Martinez",
    role: "Homeowner",
    location: "Tampa, FL",
    content: "Finally, a platform that connects me with reliable service professionals. I can't wait for ProConnect to launch - the founding member benefits are incredible!",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1550433830-5640b04d8134",
    alt: "Sarah Martinez, a smiling woman with shoulder-length brown hair wearing a casual blue top"
  },
  {
    name: "Mike Thompson",
    role: "HVAC Contractor",
    location: "Orlando, FL",
    content: "As a service provider, I'm excited about ProConnect's approach. The platform seems designed to actually help professionals grow their business, not just take a cut.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1690865617558-24d8e7970b86",
    alt: "Mike Thompson, a professional-looking man with short brown hair and a friendly smile wearing a collared shirt"
  },
  {
    name: "Jennifer Chen",
    role: "Business Owner",
    location: "Tampa, FL",
    content: "The founding member benefits are amazing - $50 credit, priority service, and lifetime app access? Plus being featured on the Wall of Founders for my business is perfect for networking.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1636393913936-187da0a372e3",
    alt: "Jennifer Chen, a confident businesswoman with long black hair wearing professional attire"
  },
  {
    name: "Robert Davis",
    role: "Property Manager",
    location: "Lakeland, FL",
    content: "Managing multiple properties, I need reliable service providers. ProConnect's expansion plan covers all my markets - Tampa, Orlando, and beyond. Can't wait!",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1631894862790-b8bf1598eb85",
    alt: "Robert Davis, a middle-aged man with gray hair and beard wearing a professional blazer"
  },
  {
    name: "Lisa Rodriguez",
    role: "Homeowner",
    location: "Tampa, FL",
    content: "I\'ve been burned by unreliable contractors before. ProConnect\'s focus on quality and their founding member program shows they\'re serious about building trust.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1678935903557-98ae7d37e1ca",
    alt: "Lisa Rodriguez, a woman with curly brown hair smiling warmly wearing a casual sweater"
  },
  {
    name: "David Kim",
    role: "Electrical Contractor",
    location: "Orlando, FL",
    content: "The priority service feature for founding members is genius - it shows ProConnect values long-term relationships over quick profits. That\'s the kind of platform I want to be part of.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1690672130640-eea047155a40",
    alt: "David Kim, a young professional man with short black hair wearing a casual button-up shirt"
  }];


  return (
    <section id="testimonials-section" className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-6 py-3 text-sm font-semibold mb-8">
            <Star className="w-4 h-4" />
            Community Testimonials
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            What <span className="text-transparent bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text">Future Members</span> Are Saying
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Hear from Florida residents and service professionals who are excited about 
            ProConnect's launch and founding member benefits.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {testimonials?.map((testimonial, index) =>
          <div
            key={index}
            className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden">

              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity duration-300">
                <Quote className="w-12 h-12 text-primary" />
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(testimonial?.rating)]?.map((_, i) =>
              <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
              )}
              </div>

              {/* Content */}
              <p className="text-gray-700 leading-relaxed mb-8 relative z-10">
                "{testimonial?.content}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <img
                src={testimonial?.avatar}
                alt={testimonial?.alt}
                className="w-14 h-14 rounded-2xl object-cover border-2 border-gray-100" />

                <div>
                  <div className="font-bold text-gray-900 group-hover:text-primary transition-colors duration-300">
                    {testimonial?.name}
                  </div>
                  <div className="text-sm text-gray-600">
                    {testimonial?.role}
                  </div>
                  <div className="text-sm text-primary font-medium">
                    {testimonial?.location}
                  </div>
                </div>
              </div>

              {/* Hover Effect Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
            </div>
          )}
        </div>

        {/* Community Stats */}
        <div className="bg-gradient-to-r from-primary to-blue-700 rounded-3xl p-8 md:p-12 text-center">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-4xl md:text-5xl font-black text-white mb-2">500+</div>
              <div className="text-white/90 font-semibold">Interested Professionals</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-orange-300 mb-2">15k+</div>
              <div className="text-white/90 font-semibold">Newsletter Subscribers</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-black text-white mb-2">4.9★</div>
              <div className="text-white/90 font-semibold">Expected Rating</div>
            </div>
          </div>

          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Join the Community That's Building the Future
          </h3>
          
          <button
            className="bg-white text-primary font-bold text-lg px-10 py-4 rounded-2xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
            onClick={() => document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' })}>

            Become a Founding Member
          </button>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;